// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppIcon } from '@loa/ui-components';
import { authApi } from '../api/authApi';
import './RegistrationSuccessPage.css';

export function RegistrationSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [completing, setCompleting] = useState(true);
  const [isFirstTimeActivation, setIsFirstTimeActivation] = useState(false);

  useEffect(() => {
    const completeRegistration = async () => {
      // Try to get token from URL query parameter first, then from sessionStorage
      const urlToken = searchParams.get('token');
      const sessionToken = sessionStorage.getItem('registration_token');
      const token = urlToken || sessionToken;

      if (!token) {
        // No token found - advisor already active, redirect immediately
        console.warn('No registration token found - advisor already active');
        navigate('/');
        return;
      }

      try {
        // Call backend to mark registration complete and set advisor status to 'active'
        await authApi.completeRegistration(token);

        // Clear the token from sessionStorage
        sessionStorage.removeItem('registration_token');

        // First time activation - show success message
        setIsFirstTimeActivation(true);
        setCompleting(false);
      } catch (err) {
        console.error('Failed to complete registration:', err);
        // If completion fails (e.g., token already used), advisor is already active
        // Redirect immediately without timeout
        sessionStorage.removeItem('registration_token');
        navigate('/');
      }
    };

    completeRegistration();
  }, [navigate, searchParams]);

  useEffect(() => {
    // Only start redirect timer for first-time activations
    if (completing || !isFirstTimeActivation) return;

    // Redirect to dashboard after 5 seconds
    const timer = setTimeout(() => {
      navigate('/');
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate, completing, isFirstTimeActivation]);

  if (completing) {
    return (
      <div className="registration-success-page">
        <div className="registration-success-card" style={{ alignItems: 'center' }}>
          <div className="registration-success-spinner">
            <div className="registration-success-spinner-track" />
            <div className="registration-success-spinner-head" />
          </div>
          <p className="registration-success-loading">
            Completing your registration...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-success-page">
      <div className="registration-success-card">
        <div className="registration-success-content">
          <AppIcon name="tick circle" size={80} color="#00A63E" />
          <h1 className="registration-success-title">Registration complete</h1>
          <p className="registration-success-subtitle">
            Your account is now active. You can now log in to the advisor portal and start managing your clients.
          </p>
        </div>
        <p className="registration-success-redirect">
          Redirecting to dashboard in a few seconds
        </p>
      </div>
    </div>
  );
}
